import { Tooltip, type TreeDataNode as DataNode } from "antd";
import t from "../../app/i18n";
import AntIcon from "../../common/components/icons/AntIcon";
import { Feature, Permission, PermissionCategory } from "../../common/security/enums";
import { PermissionDefinition, PermissionDefinitions } from "../enumerations/types";
import { AgentUserAccount, AgentUserProfile, ClientUserAccount, ClientUserProfile, UserAdminView } from "./types";

// category keys share the tree with permission keys and some names collide (e.g. COMMISSIONS)
const getCategoryTreeKey = (category: PermissionCategory) => "category-" + category;

const getPermissionDefinition = (definitions: PermissionDefinitions, permission: Permission) =>
  definitions.permissions.find((definition) => definition.permission === permission);

const hasMissingPrimaryPrerequisites = (
  definition: PermissionDefinition | undefined,
  checkedPermissions: Permission[],
) => !!definition?.prerequisites.some((prerequisite) => !checkedPermissions.includes(prerequisite));

const hasMissingSecondaryPrerequisites = (
  definition: PermissionDefinition | undefined,
  checkedPermissions: Permission[],
) =>
  !!definition?.secondaryPrerequisites.length &&
  !definition.secondaryPrerequisites.some((prerequisite) => checkedPermissions.includes(prerequisite));

export const isSystemAdminOnlyPermission = (definitions: PermissionDefinitions | undefined, permission: Permission) =>
  definitions && getPermissionDefinition(definitions, permission)?.systemAdminOnly;

/**
 * Repeated until stable - dropping a permission may break the "at least one of" secondary prerequisite
 * of another permission that was evaluated against the original selection.
 */
export const filterPermissionsWithPrerequisites = (
  definitions: PermissionDefinitions | undefined,
  checkedPermissions: Permission[],
): Permission[] => {
  if (!definitions) {
    return checkedPermissions;
  }

  const filtered = checkedPermissions.filter((permission) => {
    const definition = getPermissionDefinition(definitions, permission);
    return (
      !hasMissingPrimaryPrerequisites(definition, checkedPermissions) &&
      !hasMissingSecondaryPrerequisites(definition, checkedPermissions)
    );
  });

  return filtered.length === checkedPermissions.length
    ? filtered
    : filterPermissionsWithPrerequisites(definitions, filtered);
};

const getAvailablePermissions = (
  definitions: PermissionDefinitions,
  allowedFeatures: Feature[],
  isCurrentUserSystemAdmin?: boolean,
) => {
  const allowedFeaturesPermissions = definitions.features
    .filter(({ feature }) => allowedFeatures.includes(feature))
    .flatMap(({ permissions }) => permissions);

  return definitions.categories
    .map(({ category, permissions }) => ({
      category,
      permissions: permissions.filter((permission) => {
        const definition = getPermissionDefinition(definitions, permission);
        return (
          (isCurrentUserSystemAdmin || !definition?.systemAdminOnly) &&
          (definition?.alwaysAllowed || allowedFeaturesPermissions.includes(permission))
        );
      }),
    }))
    .filter(({ permissions }) => permissions.length > 0);
};

export const buildPermissionTreeNodesView = (
  definitions: PermissionDefinitions | undefined,
  allowedFeatures: Feature[],
  accountPermissions: Permission[],
  isCurrentUserSystemAdmin?: boolean,
): DataNode[] => {
  if (!definitions) {
    return [];
  }

  return getAvailablePermissions(definitions, allowedFeatures, isCurrentUserSystemAdmin).map<DataNode>(
    ({ category, permissions }) => ({
      title: t("user.enums.permissionsCategory." + category),
      key: getCategoryTreeKey(category),
      value: getCategoryTreeKey(category),
      selectable: false,
      disabled: !permissions.some((permission) => accountPermissions.includes(permission)),
      children: permissions.map<DataNode>((permission) => ({
        title: t("user.enums.permission." + permission),
        key: permission,
        value: permission,
        selectable: false,
        disabled: !accountPermissions.includes(permission),
      })),
    }),
  );
};

export const buildPermissionTreeNodes = (
  definitions: PermissionDefinitions | undefined,
  allowedFeatures: Feature[],
  checkedPermissions: Permission[] = [],
  isCurrentUserSystemAdmin?: boolean,
  isModifiedUserSystemAdmin?: boolean,
): DataNode[] => {
  if (!definitions) {
    return [];
  }

  return getAvailablePermissions(definitions, allowedFeatures, isCurrentUserSystemAdmin).map<DataNode>(
    ({ category, permissions }) => ({
      title: t("user.enums.permissionsCategory." + category),
      key: getCategoryTreeKey(category),
      value: getCategoryTreeKey(category),
      selectable: false,
      checkable: false,
      disabled: !permissions.some((permission) => checkedPermissions.includes(permission)),
      children: permissions.map<DataNode>((permission) => {
        const definition = getPermissionDefinition(definitions, permission);

        const missingPrimaryPrerequisites = hasMissingPrimaryPrerequisites(definition, checkedPermissions);
        const missingSecondaryPrerequisites = hasMissingSecondaryPrerequisites(definition, checkedPermissions);
        const missingSystemAdmin = !!definition?.systemAdminOnly && !isModifiedUserSystemAdmin;

        const disabled = missingPrimaryPrerequisites || missingSecondaryPrerequisites || missingSystemAdmin;
        const title = disabled ? (
          <>
            {t("user.enums.permission." + permission)}
            <Tooltip
              placement="right"
              title={
                <>
                  {missingPrimaryPrerequisites && (
                    <>
                      {t("user.helpers.missingPrerequisites")}:&nbsp;
                      {definition?.prerequisites
                        .filter((prerequisite) => !checkedPermissions.includes(prerequisite))
                        .map((prerequisite) => t("user.enums.permission." + prerequisite))
                        .join(", ")}
                      .&nbsp;
                    </>
                  )}
                  {missingSecondaryPrerequisites && (
                    <>
                      {t("user.helpers.missingSecondaryPrerequisites")}:&nbsp;
                      {definition?.secondaryPrerequisites
                        .map((prerequisite) => t("user.enums.permission." + prerequisite))
                        .join(", ")}
                      .&nbsp;
                    </>
                  )}
                  {missingSystemAdmin && <>{t("user.helpers.missingSystemAdmin")}</>}
                </>
              }
            >
              <span>
                <AntIcon type="question" className="margin-left-tiny cursor-help" />
              </span>
            </Tooltip>
          </>
        ) : (
          t("user.enums.permission." + permission)
        );

        return { title, key: permission, selectable: false, disabled, disableCheckbox: disabled };
      }),
    }),
  );
};

export const hasConfirmedAgentUserAccount = (user: UserAdminView): boolean =>
  !!user.agentUserRole && user.agentUserRole.userAccounts.some((userAccount) => userAccount.confirmed);

export const hasConfirmedClientUserAccount = (user: UserAdminView): boolean =>
  !!user.clientUserRole && user.clientUserRole?.userAccounts.some((userAccount) => userAccount.confirmed);

export const hasDisabledAgentUserAccountsOnly = (user: UserAdminView): boolean =>
  !!user.agentUserRole && user.agentUserRole?.userAccounts.every((userAccount) => userAccount.disabled);

export const isAgentUserAccount = (
  account: AgentUserAccount | ClientUserAccount | undefined,
): account is AgentUserAccount => !!account && "agent" in account;

export const isAgentUserProfile = (
  userProfile: AgentUserProfile | ClientUserProfile | undefined,
): userProfile is AgentUserProfile => !!userProfile && "agent" in userProfile;
